/**
 * Broadcast channels' rules (docs/broadcast-channels-plan.md D836–D872): who
 * runs a channel and what they may do, and the rules a channel's messages are
 * judged by. The TypeScript twin of `wire/src/broadcast.rs` — a copy, as
 * `LinkImage::is_large` is in `links.ts`, because this client cannot link that
 * crate without a wasm rebuild — line for line, so a menu here never offers
 * what the gateway refuses (D845, D407's `deletable_for_everybody_by`).
 * `broadcast.test.ts` repeats the Rust module's own tests.
 *
 * Rights and abilities travel as words (D844): a word this build cannot name is
 * kept and sent back untouched, and here it is simply no bit.
 */
import type { Message } from "../gen/dto";
import { DELETE_WINDOW_MS } from "../store/types";

/** A right on a channel's staff (D844), in bit order — `StaffRight::ALL`, the
 * order the rights dialog lists them in. */
export const STAFF_RIGHTS = [
  "post",
  "edit_posts",
  "delete_posts",
  "post_options",
  "comment_as_channel",
  "moderate_comments",
  "restrict",
  "ban",
  "edit_info",
  "invite",
  "settings",
] as const;

export type StaffRight = (typeof STAFF_RIGHTS)[number];

/** Something a person does in a channel (D852, D853), in bit order —
 * `Ability::ALL`. */
export const ABILITIES = [
  "comment",
  "react",
  "sticker",
  "voice",
  "video_note",
  "media",
  "file",
  "link",
] as const;

export type Ability = (typeof ABILITIES)[number];

/** The six kinds a channel may let its comments carry (D852) — `Ability::KINDS`. */
export const COMMENT_KIND_WORDS: readonly Ability[] = [
  "sticker",
  "voice",
  "video_note",
  "media",
  "file",
  "link",
];

export function rightBit(right: StaffRight): number {
  return 1 << STAFF_RIGHTS.indexOf(right);
}

export function abilityBit(ability: Ability): number {
  return 1 << ABILITIES.indexOf(ability);
}

/** Every right's bit — `ALL_RIGHTS`. */
export const ALL_RIGHTS = (1 << STAFF_RIGHTS.length) - 1;

/** What a server administrator holds in every channel without being on its
 * staff (D844): the moderation four — `SERVER_ADMIN_RIGHTS`. */
export const SERVER_ADMIN_RIGHTS =
  rightBit("delete_posts") | rightBit("moderate_comments") | rightBit("restrict") | rightBit("ban");

/** What a new channel's comments may carry (D852): every kind — `COMMENT_KINDS`,
 * 252. */
export const COMMENT_KINDS = COMMENT_KIND_WORDS.reduce((bits, a) => bits | abilityBit(a), 0);

/** The bits of a list of rights. Unlike `rights_bits` this never fails: a word
 * this build cannot name is the gateway's to judge, and holds no bit here. */
export function rightsBits(words: readonly string[]): number {
  let bits = 0;
  for (const word of words) {
    const at = (STAFF_RIGHTS as readonly string[]).indexOf(word);
    if (at !== -1) bits |= 1 << at;
  }
  return bits;
}

/** The wire list of a set of rights, in bit order — `rights_words`. */
export function rightsWords(bits: number): StaffRight[] {
  return STAFF_RIGHTS.filter((_, i) => (bits & (1 << i)) !== 0);
}

/** The bits of a list of abilities; an unknown word holds no bit. */
export function abilitiesBits(words: readonly string[]): number {
  let bits = 0;
  for (const word of words) {
    const at = (ABILITIES as readonly string[]).indexOf(word);
    if (at !== -1) bits |= 1 << at;
  }
  return bits;
}

/** The wire list of a set of abilities, in bit order — `abilities_words`. */
export function abilitiesWords(bits: number): Ability[] {
  return ABILITIES.filter((_, i) => (bits & (1 << i)) !== 0);
}

/** Where somebody stands in a broadcast channel (D845) — what every right is
 * asked of. A thread's standing is its channel's (D846). */
export type Standing = { t: "nobody" } | { t: "staff"; bits: number } | { t: "owner" };

/** `Standing::of`: the owner, a server administrator's moderation four with
 * whatever staff rights they hold besides, a staff member's own bits, or
 * nobody. */
export function standingOf(owner: boolean, serverAdmin: boolean, staffRights: number): Standing {
  if (owner) return { t: "owner" };
  const held = serverAdmin ? SERVER_ADMIN_RIGHTS : 0;
  const bits = (staffRights & ALL_RIGHTS) | held;
  return bits === 0 ? { t: "nobody" } : { t: "staff", bits };
}

/** Every right a standing holds — `Standing::bits`. */
export function standingBits(standing: Standing): number {
  switch (standing.t) {
    case "nobody":
      return 0;
    case "staff":
      return standing.bits & ALL_RIGHTS;
    case "owner":
      return ALL_RIGHTS;
  }
}

/** Whether a standing is told who runs the channel (D842) — `Standing::is_staff`. */
export function isStaff(standing: Standing): boolean {
  return standing.t !== "nobody";
}

/** Whether `standing` holds `right` (D845): the one question every right is
 * asked through. */
export function may(standing: Standing, right: StaffRight): boolean {
  return (standingBits(standing) & rightBit(right)) !== 0;
}

/** How long after its publishing a post may still be edited (D867): a day, on
 * the gateway's clock — `POST_EDIT_WINDOW_MS`. */
export const POST_EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** The part of a message these rules read. */
type Judged = Pick<Message, "sender_id" | "sent_at_ms" | "posted_by">;

/** Who wrote this message as far as the reader's copy says — `Message::author`:
 * the person behind a message in the channel's name when the copy names them
 * (`posted_by`, the staff's), its sender otherwise, 0 when nobody. */
export function author(m: Judged): number {
  return m.posted_by ?? m.sender_id;
}

/** `Message::post_edit_window_open` (D867), at `nowMs` on the gateway's clock. */
export function postEditWindowOpen(m: Judged, nowMs: number): boolean {
  return nowMs - m.sent_at_ms <= POST_EDIT_WINDOW_MS;
}

/** `Message::delete_window_open` (chat-plan.md D410): four hours. */
function deleteWindowOpen(m: Judged, nowMs: number): boolean {
  return nowMs - m.sent_at_ms <= DELETE_WINDOW_MS;
}

/** `post_editable_by` (D850, D867): within the day, its publisher while they
 * hold `post`, and any holder of `edit_posts`. */
export function postEditableBy(
  post: Judged,
  editor: number,
  standing: Standing,
  nowMs: number,
): boolean {
  return (
    postEditWindowOpen(post, nowMs) &&
    (may(standing, "edit_posts") ||
      (editor !== 0 && author(post) === editor && may(standing, "post")))
  );
}

/** `post_deletable_by` (D850, D1022): at any age, its publisher while they
 * hold `post`, a holder of `delete_posts`, the owner and a server
 * administrator — for everybody; nobody else deletes it at all. */
export function postDeletableBy(post: Judged, deleter: number, standing: Standing): boolean {
  return (
    may(standing, "delete_posts") ||
    (deleter !== 0 && author(post) === deleter && may(standing, "post"))
  );
}

/** `post_switchable_by` (D851): its publisher while they hold `post`, a holder
 * of `post_options`, the owner. */
export function postSwitchableBy(post: Judged, who: number, standing: Standing): boolean {
  return (
    may(standing, "post_options") || (who !== 0 && author(post) === who && may(standing, "post"))
  );
}

/** `comment_deletable_for_everybody_by` (D850, D868, D1022): a holder of
 * `moderate_comments`, the owner and a server administrator at any age; its
 * writer within the four hours — for everybody; nobody else removes it at
 * all. */
export function commentDeletableForEverybodyBy(
  comment: Judged,
  deleter: number,
  standing: Standing,
  nowMs: number,
): boolean {
  return (
    may(standing, "moderate_comments") ||
    (deleter !== 0 && author(comment) === deleter && deleteWindowOpen(comment, nowMs))
  );
}

/** Whether a link segment names a channel by its handle rather than by its id
 * (D871): a handle starts with a letter, an id is digits. The shape is the
 * gateway's to judge in full (`echo_wire::broadcast::handle`); this is only
 * what tells the two apart, and what the creation form asks before sending.
 * Latin letters and digits joined by single `_` or `-`, 3 to 64 of them,
 * starting with a letter and not ending in `_pet` (D732, D384, D870). */
export function looksLikeHandle(raw: string): boolean {
  return (
    raw.length >= 3 &&
    raw.length <= 64 &&
    /^[A-Za-z][A-Za-z0-9]*(?:[_-][A-Za-z0-9]+)*$/.test(raw) &&
    !raw.toLowerCase().endsWith("_pet")
  );
}
